import * as geojson from "geojson";
import _ from "lodash";

import type {EqualCriteria} from "@esWebAPI/es/model/Criteria";
import type {RelatedLayerViewMapping} from "@esWebAPI/es/map/TerritoryModel";
import type {TerritoryBaseLayer} from "@esWebAPI/es/map/layer/territory/TerritoryBaseLayer";
import type {TerritoryPolygonRelatedLayer} from "@esWebAPI/es/map/layer/territory/TerritoryPolygonRelatedLayer";

/**
     * Mapping between related feature ids and base feature ids
     * key: [relatedLayerMetaId_baseLayerMetaId]
     * value: {attr: criteriaSelectAttr, mapping: relatedFeatureId => list of base features id}
     */
export  class TerritoryRelatedFeaturesMapping {

    private featureViewModel: any;

    private mapping: {
        [key: string]: {
            attr: string,
            mapping: {
                [relatedFeatureId: number]: number[]
            }
        }
    } = {};

    private readonly relatedLayerViews: { [p: number]: number };
    private readonly baseLayerMetaId: number;

    constructor(featureViewModel: any, relatedLayerViewMapping: RelatedLayerViewMapping) {
        this.featureViewModel = featureViewModel;
        this.relatedLayerViews = relatedLayerViewMapping['relatedLayerView'];
        this.baseLayerMetaId = Object.values(relatedLayerViewMapping['baseLayerView'])[0] as number;

        const layerViewIds = _.keys(this.relatedLayerViews).map( k => +k);
        for (const lvId of layerViewIds) {
            const layerView = this.featureViewModel.getLayerById(lvId);
            const key = this.getKey(lvId);

            if (!this.mapping[key]) {
                this.mapping[key] = {
                    attr: layerView.getSelectCriteria().attr,
                    mapping: {}
                };
            }
        }
    }

    private getKey(layerViewId: number){
        return this.relatedLayerViews[layerViewId] + "_" + this.baseLayerMetaId;
    }

    public load(baseLayer: TerritoryBaseLayer, relatedLayers: TerritoryPolygonRelatedLayer[]): JQuery.Promise<void> {
        const batchSize = 2000;

        const featIdsPerLv = new Map<number, Map<string, number>>();
        const layerPromises: Promise<void>[] = relatedLayers.map(rl => {
            const featMap = new Map<string, number>();
            featIdsPerLv.set(rl.layerView.id, featMap);

            return new Promise<void>((resolve) => {
               rl.featureWorker.getAll().then((features: geojson.Feature[]) => {
                    const attr = (rl.layerView.selectCriteria as EqualCriteria<any>).attr;

                    const processBatch = (start: number) => {
                        for (let i = start; i < Math.min(start + batchSize, features.length); i++) {
                            const feat = features[i];
                            if (feat.properties && feat.id) {
                                const attrVal = feat.properties[attr];
                                featMap.set(attrVal, +feat.id);
                            }
                        }
                        if (start + batchSize < features.length) {
                            setTimeout(() => processBatch(start + batchSize), 0);
                        } else {
                            resolve();
                        }
                    };

                    processBatch(0);
                });
            });
        });

        return $.when(...layerPromises).then(() => {

            const attrs = relatedLayers.map(rl => (rl.layerView.selectCriteria as EqualCriteria<any>).attr);

            const processBaseFeaturesBatch = (start: number) => {
                baseLayer.featureWorker.getLean(attrs, start, batchSize).then((data: any[]) => {

                    for (let i = 0; i < data.length; i++) {

                        const baseFeatureId = data[i];

                        relatedLayers.forEach((rl) => {

                            const key = this.getKey(rl.layerView.id);
                            const linkAttrVal = data[++i];
                            const relatedFeatId = featIdsPerLv.get(rl.layerView.id)?.get(linkAttrVal);

                            if (relatedFeatId !== undefined) {
                                if (!this.mapping[key].mapping.hasOwnProperty(relatedFeatId)) {
                                    this.mapping[key].mapping[relatedFeatId] = [];
                                }
                                this.mapping[key].mapping[relatedFeatId].push(baseFeatureId as number);
                            }
                        });
                    }

                    if (data.length > 0) {
                        setTimeout(() => processBaseFeaturesBatch(start + batchSize), 0);
                    } else {
                        return es.util.deferred.resolved();
                    }
                });
            };

            return processBaseFeaturesBatch(0);
        });
    }

    public add(baseFeatures: any[]) {

        function getAttrs(feature: any) {
            if (feature.tags)
                return feature.tags;
            if (feature.attrs)
                return feature.attrs;
            if (feature.properties)
                return feature.properties;
            return null;
        }

        for (const feature of baseFeatures) {
            const attrs = getAttrs(feature);

            if(!attrs)
                continue;

            const keys = _.keys(this.mapping);
            for (const key of keys) {
                const attr = this.mapping[key].attr;
                const map = this.mapping[key].mapping;

                const attrValue = attrs[attr]; // Country or State id

                if(!map[attrValue]) {
                    map[attrValue] = [];
                }
                map[attrValue].push(feature.id);
            }
        }
    }

    public remove(_baseFeatures: any[]) {
        console.warn("remove not implemented", _baseFeatures);
    }

    public getForLayer(layerViewId: number){
        const key = this.getKey(layerViewId);
        return this.mapping[key].mapping;
    }

    /**
     * Get base layer features for the related feature id
     * @param layerViewId LayerView of the related feature
     * @param featureId Id of the related feature, uses ES_ID, not link attr value.
     */
    public get(layerViewId: number, featureId: number): number[] {
        return this.getForLayer(layerViewId)[featureId];
    }
}
